import { FC, JSX, useEffect, useRef, useState } from 'react';
import { useSchedule } from 'features/index';
import { useAppSelector } from 'shared/lib';

import { DateHeader } from '../dateHeader';
import { useCardClickModal, useScroll } from '../hooks';
import { getWeeksDates, makeCards } from '../lib/utils';
import { ModalCardSchedule } from '../modalCardSchedule';
import { SkeletonSchedule } from '../skeletonSchedule';

import styles from './styles.module.scss';

export const StageCalendarSchedule: FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const MAX_MODAL_WIDTH = 350;

  const { startIntervalDate, isGroupSchedule, endIntervalDate } =
    useAppSelector((state) => state.schedulePublicReducer);

  const {
    isSkeleton,
    filterSchedule,
    dataForGroupSchedule,
    dataForTeacherSchedule,
  } = useSchedule();

  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsScrolled(!entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      },
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const { firstWeekMonday, lastWeekSunday } =
      getWeeksDates(startIntervalDate);
    filterSchedule(firstWeekMonday, lastWeekSunday);
  }, [startIntervalDate, endIntervalDate]);

  const {
    containerScheduleRef,
    handleMouseDown,
    handleMouseLeave,
    handleMouseMove,
    handleMouseUp,
    isDragging,
  } = useScroll();

  const {
    closeModal,
    handleCardClick,
    modalPosition,
    selectedDateData,
    selectedDaySchedule,
  } = useCardClickModal(calendarRef, MAX_MODAL_WIDTH);

  const cards: JSX.Element[] = makeCards(
    isGroupSchedule,
    isGroupSchedule ? dataForGroupSchedule : dataForTeacherSchedule,
    startIntervalDate,
    handleCardClick,
  );

  const makeRows = () => {
    const rows: JSX.Element[] = [];
    for (let i = 0; i < Math.ceil(cards.length / 7); i++) {
      const start = i * 7;
      const end = start + 7;

      rows.push(
        <div className={styles['calendar-schedule__row']} key={i}>
          {cards.slice(start, end)}
        </div>,
      );
    }
    return rows;
  };

  return (
    <div
      className={styles['stage-calendar-schedule']}
      style={{ cursor: isDragging ? 'grabbing' : 'default' }}
      ref={containerScheduleRef}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
    >
      <DateHeader isScrolled={isScrolled} />
      <div ref={sentinelRef} className={styles['scroll-sentinel']} />
      <div className={styles['container-schedule']} ref={calendarRef}>
        {isSkeleton ? (
          <SkeletonSchedule />
        ) : (
          cards.length % 7 === 0 && (
            <div className={styles['content']}>{makeRows()}</div>
          )
        )}

        {!isSkeleton && selectedDaySchedule && selectedDateData && (
          <ModalCardSchedule
            dataSchedule={selectedDaySchedule}
            dateData={selectedDateData}
            position={modalPosition}
            isGroupSchedule={isGroupSchedule}
            onClose={closeModal}
          />
        )}
      </div>
    </div>
  );
};
